import { Component, OnInit, ViewEncapsulation, Input } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { County } from 'src/app/angular-elements/shared/interfaces/zip-county-response.interface';
import { NgRedux } from '@ptl/angular-redux-store';
import { StateStructure } from '@ptl/state';
import { ProducerService } from '../producer.service';
import { saveAgency, saveOtherUserInfo } from '../configs/reducers/producer.reducer';
import {
  LOGIN_INFO_REDUCER_NAME,
  UserInfo,
  UserInfoStateStructure,
  SeniorAgency,
  LoggedInUserInformationService,
  EntitlementsHelperService,
  LINE_OF_BUSINESS_ENUM,
  IndividualMarketAgency,
  BaseAgency,
  setSelectedRegionOrState,
  SelectedAgencyStateStructure,
  SELECTED_AGENCY_REDUCER_NAME,
  setAgencyPreferences,
  setLobAgencyPreferences
} from '@ptl/account-management';
import { CommonUtilityService } from 'src/app/shared/utility-services/common-utility.service';
import { PtlWindowLocationService } from '@ptl/global-components';
import { ZipCountyStateStructure } from 'src/app/angular-elements/shared/interfaces/zipCountyStateStructure.interface';
import { OtherUserInfo } from '../configs/interfaces/otherUserInfo.interface';
import { PrePopulatedDataStateStructure } from '../../shared/components/configs/interfaces/prePopulatedDataStateStructure.interface';
import { AuthoringModeService } from 'src/app/shared/services/authoring-mode.service';
import { setPlanChange } from '../../plan-selection/configs/reducers/plans.reducer';

import { GlobalShadowStyleDirective } from '@ptl/global-components';

//forms only change, dummy commit for release

export interface AgencyFormControls {
  agency: AbstractControl;
  npnNumber: AbstractControl;
  rememberSelection: AbstractControl;
  isOnCallWithFieldAgent: AbstractControl;
}

export interface AgencyFormAsJson {
  agency: SeniorAgency | IndividualMarketAgency;
  npnNumber: string;
  rememberSelection: boolean;
  isOnCallWithFieldAgent: string;
}

@Component({
    selector: 'app-agency-page',
    templateUrl: './agency-page.component.html',
    styleUrls: ['./agency-page.component.scss'],
    encapsulation: ViewEncapsulation.ShadowDom,
    standalone: false
})
export class AgencyPageComponent extends GlobalShadowStyleDirective implements OnInit {
  @Input() viewPlanLink: string;
  @Input() hideZipcodeString: string = 'false';
  hideZipcode: boolean = false;

  agencyForm: UntypedFormGroup;

  agenciesForDisplay: BaseAgency[];
  userInfo: UserInfo;
  loginInfo: UserInfoStateStructure;

  selectedMarket: LINE_OF_BUSINESS_ENUM;

  linesOfBusiness = {
    senior: LINE_OF_BUSINESS_ENUM.SENIOR,
    individual: LINE_OF_BUSINESS_ENUM.INDIVIDUAL,
    smallGroup: LINE_OF_BUSINESS_ENUM.SMALL_GROUP
  };

  rememberPrefCheckbox = true;
  preferredAgencyMessage = '';

  /** Stores the selected county */
  saveableCounty: County;
  saveableZipCode: string;
  zipCodeRegion: string;
  stateValues: { zipCode: string };
  agencies: string[];
  regions: string[];
  regionMismatch: boolean;
  isFieldAgent: boolean;

  constructor(
    private fb: UntypedFormBuilder,
    private reducerUtilityService: ReducerUtilityService,
    private ngRedux: NgRedux<StateStructure>,
    private producerService: ProducerService,
    private commonUtilityService: CommonUtilityService,
    private loggedInUserService: LoggedInUserInformationService,
    private windowLocationService: PtlWindowLocationService,
    private entitlementsHelperService: EntitlementsHelperService,
    private authoringMode: AuthoringModeService
  ) {
    super();
    this.regionMismatch = false;
    this.agencyForm = this.fb.group({
      agency: this.fb.control('', [Validators.required]),
      npnNumber: this.fb.control('', [Validators.required]),
      rememberSelection: this.fb.control(false, []),
      isOnCallWithFieldAgent: this.fb.control(false, [Validators.required])
    });
  }

  get formControls(): AgencyFormControls {
    return this.agencyForm as unknown as AgencyFormControls;
  }

  get formValuesAsJson(): AgencyFormAsJson {
    return this.agencyForm.value as unknown as AgencyFormAsJson;
  }

  async ngOnInit() {
    if (this.authoringMode.getAuthoringMode()) {
      this.isFieldAgent = true;
    }
    if (this.hideZipcodeString === 'true') {
      this.hideZipcode = true;
    }

    const prePopulateData: PrePopulatedDataStateStructure = this.reducerUtilityService.getState('PrePopulateData');
    const zipcountyState: ZipCountyStateStructure = this.reducerUtilityService.getState(
      'ZipCounty'
    );

    if (zipcountyState.zipCode) {
      this.stateValues = {
        zipCode: zipcountyState.zipCode
      };
    } else if (prePopulateData.prepopulatedFields && prePopulateData.prepopulatedFields.zipCode) {
      this.commonUtilityService.setBrandByZipCode(prePopulateData.prepopulatedFields.zipCode)
    }

    const selectedAgencyState: SelectedAgencyStateStructure = this.reducerUtilityService.getReducerFromApplicationState(
      SELECTED_AGENCY_REDUCER_NAME
    );

    /** Default to senior if no agency selected */
    this.selectedMarket = selectedAgencyState.selectedMarket || LINE_OF_BUSINESS_ENUM.SENIOR;

    this.loginInfo = this.reducerUtilityService.getReducerFromApplicationState(
      LOGIN_INFO_REDUCER_NAME
    ) as UserInfoStateStructure;

    this.userInfo = this.loginInfo.userInfo;
    this.agenciesForDisplay = this.entitlementsHelperService.getAgenciesByLineOfBusiness(this.selectedMarket);
    this.isFieldAgent = this.loggedInUserService.isMedicareFieldAgent();


    for (const agency of this.agenciesForDisplay) {

      if (this.selectedMarket === LINE_OF_BUSINESS_ENUM.SENIOR) {

        const seniorAgency = agency as SeniorAgency;

        if (
          seniorAgency.agencyId === this.userInfo.agencyIdPref &&
          seniorAgency.region === this.userInfo.regionPref
        ) {

          if (this.userInfo.agencyIdPref && this.userInfo.regionPref) {
            this.preferredAgencyMessage = `${seniorAgency.agencyName} - ${seniorAgency.region}`
          }

          this.agencyForm.controls['agency'].setValue(seniorAgency);

          if (seniorAgency.npnNo) {
            this.agencyForm.controls['npnNumber'].setValue(seniorAgency.npnNo);
          } else {
            this.agencyForm.controls['npnNumber'].setValue(this.userInfo.npnNo);
          }
        }
      } else {
        const individualSmallGroupAgency = agency as IndividualMarketAgency;

        if (
          individualSmallGroupAgency.agencyId === this.userInfo.agencyIdPref &&
          individualSmallGroupAgency.region === this.userInfo.regionPref
        ) {
          this.agencyForm.controls['agency'].setValue(individualSmallGroupAgency);
          if (individualSmallGroupAgency.agencyId) {
            this.agencyForm.controls['npnNumber'].setValue(individualSmallGroupAgency.agencyId);
          } else {
            this.agencyForm.controls['npnNumber'].setValue(this.userInfo.npnNo);
          }
        }
      }

    }


  }

  agencyChange(selectedAgency: SeniorAgency | IndividualMarketAgency) {
    let npnNo = this.userInfo.npnNo;
    if ((selectedAgency as SeniorAgency).npnNo) {
      npnNo = (selectedAgency as SeniorAgency).npnNo;
    }
    this.agencyForm.controls['npnNumber'].setValue(npnNo);
    this.agencyForm.controls['npnNumber'].markAsTouched();
    this.checkRegionMismatch();
  }

  async submit(form: UntypedFormGroup) {
    // We are defining this page as the beginning of the application flow, so we are going to clear out all
    // application data at this point to start out completely fresh
    this.commonUtilityService.resetStateForNewApplicationFlow();

    const selectedAgency: SeniorAgency = this.agencyForm.controls['agency']
      .value;
    const otherUserInformation: OtherUserInfo = {
      isOnCallWithFieldAgent: this.agencyForm.controls['isOnCallWithFieldAgent'].value
    };
    selectedAgency.npnNo = this.agencyForm.controls['npnNumber'].value;
    this.ngRedux.dispatch(saveAgency(selectedAgency));
    this.ngRedux.dispatch(saveOtherUserInfo(otherUserInformation));
    // save the selected agency
    const formValuesAsJson: AgencyFormAsJson = this.formValuesAsJson;
    if (formValuesAsJson.rememberSelection) {
      this.ngRedux.dispatch(setAgencyPreferences(formValuesAsJson.agency.agencyId, formValuesAsJson.agency.region))
      await this.producerService.updateAgencyPref(
        this.loginInfo.userId,
        selectedAgency.agencyId,
        selectedAgency.region
      );
      
      this.loginInfo.userInfo.agencyInfo.preferredAgencies[this.selectedMarket] = selectedAgency;
    }

    // Signal this is not a plan change flow
    this.ngRedux.dispatch(setPlanChange(false));

    // Select this agency and emit that value
    this.loginInfo.userInfo.agencyInfo.selectedAgencies[this.selectedMarket] = selectedAgency;
    this.ngRedux.dispatch(setLobAgencyPreferences(this.loginInfo.userInfo.agencyInfo));

    if (this.saveableCounty && this.saveableZipCode) {
      this.commonUtilityService.saveCountyZipData(
        this.saveableCounty,
        this.saveableZipCode
      );
      this.windowLocationService.goToUrl(this.viewPlanLink);
    }
    if (this.hideZipcode) {
      this.windowLocationService.goToUrl(this.viewPlanLink);
    }
  }

  zipCountyChanged(value: { county: County; zipCode: string }) {
    this.saveableCounty = value.county;
    this.saveableZipCode = value.zipCode;
    this.zipCodeRegion = value.county ? value.county.region : null;
    this.checkRegionMismatch();
  }

  checkRegionMismatch() {
    const selectedAgency: SeniorAgency | IndividualMarketAgency
      = this.agencyForm.controls['agency'].value;

    const isTelesalesFlow = this.loggedInUserService.isMedicareTelesales();

    if (
      this.saveableCounty &&
      !isTelesalesFlow &&
      this.commonUtilityService.isAgent()
    ) {
      if (this.selectedMarket === LINE_OF_BUSINESS_ENUM.SENIOR) {
        this.regionMismatch = this.saveableCounty.state !== selectedAgency.region;
      } else {
        this.regionMismatch = this.saveableCounty.region !== selectedAgency.region;
      }
    }

    this.ngRedux.dispatch(setSelectedRegionOrState(selectedAgency.region));

  }

  getPreferredAgencyHint() {
    if (!this.userInfo.agencyIdPref || !this.userInfo.regionPref) {
      return 'You do not have a Preferred Agency selected.';
    }
    else if (
      this.userInfo.agencyIdPref === this.agencyForm.controls['agency'].value.agencyId &&
      this.userInfo.regionPref === this.agencyForm.controls['agency'].value.region
    ) {
      return 'Your Preferred Agency is selected.';
    }
    else {
      return 'Your Preferred Agency is ' + this.preferredAgencyMessage;
    }
  }
}

